import React, { useState, useEffect } from 'react';

interface Payment {
  id: string;
  user_id: string;
  email: string;
  amount: number;
  currency: string;
  status: string;
  gateway: string;
  reference: string;
  transaction_id: string;
  payment_type: string;
  plan_type: string;
  metadata: any;
  completed_at: string;
  created_at: string;
  user_name?: string;
  user_phone?: string;
}

interface PaymentStats {
  totalPayments: number;
  totalRevenue: number;
  completedPayments: number;
  pendingPayments: number;
  failedPayments: number;
  monthlyRevenue: number;
  averageOrderValue: number;
}

interface User {
  id: string;
  vardas: string;
  el_pastas: string;
  subscription_plan: string;
  subscription_status: string;
  subscription_expires_at: string;
}

const AdminPaymentManagement: React.FC = () => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [stats, setStats] = useState<PaymentStats>({
    totalPayments: 0,
    totalRevenue: 0,
    completedPayments: 0,
    pendingPayments: 0,
    failedPayments: 0,
    monthlyRevenue: 0,
    averageOrderValue: 0
  });
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState<'overview' | 'payments' | 'subscriptions'>('overview');
  const [filter, setFilter] = useState<'all' | 'completed' | 'pending' | 'failed'>('all');

  useEffect(() => {
    fetchPaymentData();
    fetchUsers();
  }, []);

  const fetchPaymentData = async () => {
    try {
      // Fetch all payments - we'll need to create this endpoint
      const response = await fetch('/api/admin/payments', {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        // Handle nested payments structure
        const paymentsArray = data.payments?.recent_transactions || data.payments || [];
        setPayments(paymentsArray);
        calculateStats(paymentsArray);
      }
    } catch (error) {
      console.error('Error fetching payment data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch('/api/admin/users', {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const calculateStats = (paymentsData: Payment[]) => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    const totalPayments = paymentsData.length;
    const completedPayments = paymentsData.filter(p => p.status === 'completed').length;
    const pendingPayments = paymentsData.filter(p => p.status === 'pending').length;
    const failedPayments = paymentsData.filter(p => p.status === 'failed').length;

    const totalRevenue = paymentsData
      .filter(p => p.status === 'completed')
      .reduce((sum, p) => sum + p.amount, 0);

    const monthlyRevenue = paymentsData
      .filter(p => p.status === 'completed' && new Date(p.created_at) >= startOfMonth)
      .reduce((sum, p) => sum + p.amount, 0);

    const averageOrderValue = completedPayments > 0 ? totalRevenue / completedPayments : 0;

    setStats({
      totalPayments,
      totalRevenue,
      completedPayments,
      pendingPayments,
      failedPayments,
      monthlyRevenue,
      averageOrderValue
    });
  };

  const getPlanIcon = (planType: string) => {
    switch (planType) {
      case 'basic': return '⭐';
      case 'bazinis': return '⭐';
      case 'premium': return '';
      case 'profesionalus': return '';
      case 'enterprise': return '';
      case 'imone': return '';
      default: return '';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return '#28a745';
      case 'pending': return '#ffc107';
      case 'failed': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed': return 'Sėkmingas';
      case 'pending': return 'Laukiama';
      case 'failed': return 'Nepavyko';
      default: return status;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('lt-LT');
  };

  const filteredPayments = payments.filter(payment =>
    filter === 'all' || payment.status === filter
  );

  const getUserSubscriptionStatus = (userId: string) => {
    const user = users.find(u => u.id === userId);
    if (!user) return { status: 'unknown', plan: 'unknown', expires: null };

    return {
      status: user.subscription_status,
      plan: user.subscription_plan,
      expires: user.subscription_expires_at
    };
  };

  if (loading) {
    return (
      <div className="admin-payment-management">
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <div className="spinner">⏳</div>
          <p>Kraunami mokėjimo duomenys...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-payment-management">
      {/* Navigation */}
      <div className="payment-nav" style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontFamily: 'Georgia, serif', color: '#000042', marginBottom: '1rem' }}>
           Mokėjimų valdymas
        </h2>

        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
          <button
            className={`nav-button ${activeView === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveView('overview')}
            style={{
              padding: '0.5rem 1rem',
              border: 'none',
              borderRadius: '6px',
              backgroundColor: activeView === 'overview' ? '#007bff' : '#e9ecef',
              color: activeView === 'overview' ? 'white' : '#495057',
              cursor: 'pointer'
            }}
          >
             Apžvalga
          </button>
          <button
            className={`nav-button ${activeView === 'payments' ? 'active' : ''}`}
            onClick={() => setActiveView('payments')}
            style={{
              padding: '0.5rem 1rem',
              border: 'none',
              borderRadius: '6px',
              backgroundColor: activeView === 'payments' ? '#007bff' : '#e9ecef',
              color: activeView === 'payments' ? 'white' : '#495057',
              cursor: 'pointer'
            }}
          >
             Mokėjimai
          </button>
          <button
            className={`nav-button ${activeView === 'subscriptions' ? 'active' : ''}`}
            onClick={() => setActiveView('subscriptions')}
            style={{
              padding: '0.5rem 1rem',
              border: 'none',
              borderRadius: '6px',
              backgroundColor: activeView === 'subscriptions' ? '#007bff' : '#e9ecef',
              color: activeView === 'subscriptions' ? 'white' : '#495057',
              cursor: 'pointer'
            }}
          >
             Prenumeratos
          </button>
        </div>
      </div>

      {/* Overview */}
      {activeView === 'overview' && (
        <div className="payment-overview">
          {/* Stats Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
            <div className="stat-card" style={{ padding: '1.5rem', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #dee2e6' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                <div style={{ fontSize: '2rem', marginRight: '1rem' }}></div>
                <div>
                  <h4 style={{ margin: 0, color: '#495057' }}>Bendros pajamos</h4>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#28a745' }}>
                    €{stats.totalRevenue.toFixed(2)}
                  </div>
                </div>
              </div>
              <div style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                Šį mėnesį: €{stats.monthlyRevenue.toFixed(2)}
              </div>
            </div>

            <div className="stat-card" style={{ padding: '1.5rem', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #dee2e6' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                <div style={{ fontSize: '2rem', marginRight: '1rem' }}></div>
                <div>
                  <h4 style={{ margin: 0, color: '#495057' }}>Mokėjimai</h4>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#007bff' }}>
                    {stats.totalPayments}
                  </div>
                </div>
              </div>
              <div style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                Sėkmingi: {stats.completedPayments}
              </div>
            </div>

            <div className="stat-card" style={{ padding: '1.5rem', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #dee2e6' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                <div style={{ fontSize: '2rem', marginRight: '1rem' }}></div>
                <div>
                  <h4 style={{ margin: 0, color: '#495057' }}>Vid. užsakymas</h4>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#6f42c1' }}>
                    €{stats.averageOrderValue.toFixed(2)}
                  </div>
                </div>
              </div>
              <div style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                Per užsakymą
              </div>
            </div>

            <div className="stat-card" style={{ padding: '1.5rem', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #dee2e6' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                <div style={{ fontSize: '2rem', marginRight: '1rem' }}>⏳</div>
                <div>
                  <h4 style={{ margin: 0, color: '#495057' }}>Laukiantys</h4>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ffc107' }}>
                    {stats.pendingPayments}
                  </div>
                </div>
              </div>
              <div style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                Nepavyko: {stats.failedPayments}
              </div>
            </div>
          </div>

          {/* Recent Payments */}
          <div className="recent-payments" style={{ backgroundColor: 'white', borderRadius: '8px', border: '1px solid #dee2e6', padding: '1.5rem' }}>
            <h3 style={{ marginBottom: '1rem' }}>🕒 Paskutiniai mokėjimai</h3>

            {filteredPayments.slice(0, 10).map((payment) => (
              <div key={payment.id} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '1rem',
                borderBottom: '1px solid #f8f9fa',
                marginBottom: '0.5rem'
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>
                    {getPlanIcon(payment.plan_type)} {payment.email}
                  </div>
                  <div style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                    {formatDate(payment.created_at)} • {payment.reference}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                    €{payment.amount}
                  </div>
                  <div style={{
                    fontSize: '0.9rem',
                    color: getStatusColor(payment.status),
                    fontWeight: 'bold'
                  }}>
                    {getStatusText(payment.status)}
                  </div>
                </div>
              </div>
            ))}

            {filteredPayments.length === 0 && (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
                Mokėjimų dar nėra
              </div>
            )}
          </div>
        </div>
      )}

      {/* Payments List */}
      {activeView === 'payments' && (
        <div className="payments-list">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h3> Visi mokėjimai</h3>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value as any)}
                style={{ padding: '0.5rem', borderRadius: '6px', border: '1px solid #ced4da' }}
              >
                <option value="all">Visi mokėjimai</option>
                <option value="completed">Sėkmingi</option>
                <option value="pending">Laukiantys</option>
                <option value="failed">Nepavykę</option>
              </select>
            </div>
          </div>

          <div style={{ backgroundColor: 'white', borderRadius: '8px', border: '1px solid #dee2e6', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ backgroundColor: '#f8f9fa' }}>
                <tr>
                  <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Vartotojas</th>
                  <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Planas</th>
                  <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Suma</th>
                  <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Statusas</th>
                  <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Data</th>
                  <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Nuoroda</th>
                </tr>
              </thead>
              <tbody>
                {filteredPayments.map((payment) => (
                  <tr key={payment.id} style={{ borderBottom: '1px solid #f8f9fa' }}>
                    <td style={{ padding: '1rem' }}>
                      <div>
                        <div style={{ fontWeight: 'bold' }}>{payment.email}</div>
                        <div style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                          ID: {payment.user_id.substring(0, 8)}...
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '1rem' }}>
                      {getPlanIcon(payment.plan_type)} {payment.plan_type}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <strong>€{payment.amount}</strong>
                      {payment.metadata && (payment.metadata.minutes || payment.metadata.phoneNumberCost) && (
                        <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '0.25rem' }}>
                          {payment.metadata.minutes && `${payment.metadata.minutes} min`}
                          {payment.metadata.phoneNumberCost > 0 && ` + €${payment.metadata.phoneNumberCost} tel.`}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '12px',
                        fontSize: '0.85rem',
                        fontWeight: 'bold',
                        backgroundColor: payment.status === 'completed' ? '#d4edda' :
                                       payment.status === 'pending' ? '#fff3cd' : '#f8d7da',
                        color: getStatusColor(payment.status)
                      }}>
                        {getStatusText(payment.status)}
                      </span>
                    </td>
                    <td style={{ padding: '1rem' }}>
                      {formatDate(payment.created_at)}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <code style={{ fontSize: '0.8rem', backgroundColor: '#f8f9fa', padding: '0.25rem' }}>
                        {payment.reference}
                      </code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredPayments.length === 0 && (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
                Nerasta mokėjimų pagal pasirinktą filtrą
              </div>
            )}
          </div>
        </div>
      )}

      {/* Subscriptions Management */}
      {activeView === 'subscriptions' && (
        <div className="subscriptions-management">
          <h3 style={{ marginBottom: '1.5rem' }}> Prenumeratų valdymas</h3>

          <div style={{ backgroundColor: 'white', borderRadius: '8px', border: '1px solid #dee2e6', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ backgroundColor: '#f8f9fa' }}>
                <tr>
                  <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Vartotojas</th>
                  <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Planas</th>
                  <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Statusas</th>
                  <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Galioja iki</th>
                  <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Mokėjimai</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => {
                  const userPayments = payments.filter(p => p.user_id === user.id);
                  const completedPayments = userPayments.filter(p => p.status === 'completed');

                  return (
                    <tr key={user.id} style={{ borderBottom: '1px solid #f8f9fa' }}>
                      <td style={{ padding: '1rem' }}>
                        <div>
                          <div style={{ fontWeight: 'bold' }}>{user.vardas}</div>
                          <div style={{ fontSize: '0.9rem', color: '#6c757d' }}>{user.el_pastas}</div>
                        </div>
                      </td>
                      <td style={{ padding: '1rem' }}>
                        {getPlanIcon(user.subscription_plan)} {user.subscription_plan || 'free'}
                      </td>
                      <td style={{ padding: '1rem' }}>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '12px',
                          fontSize: '0.85rem',
                          fontWeight: 'bold',
                          backgroundColor: user.subscription_status === 'active' ? '#d4edda' : '#f8d7da',
                          color: user.subscription_status === 'active' ? '#155724' : '#721c24'
                        }}>
                          {user.subscription_status || 'inactive'}
                        </span>
                      </td>
                      <td style={{ padding: '1rem' }}>
                        {user.subscription_expires_at ?
                          new Date(user.subscription_expires_at).toLocaleDateString('lt-LT') :
                          '-'
                        }
                      </td>
                      <td style={{ padding: '1rem' }}>
                        <div>
                          <strong>{completedPayments.length}</strong> sėkmingi
                        </div>
                        <div style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                          €{completedPayments.reduce((sum, p) => sum + p.amount, 0).toFixed(2)} iš viso
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {users.length === 0 && (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
                Vartotojų dar nėra
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPaymentManagement;